import { Injectable } from '@angular/core';
import { Translation } from '@ngneat/transloco';
import { createSelector, Selector } from '@ngxs/store';
import { ITenderType } from '@nibtravel/shared/util/models';

import {
  ContractContentMap,
  ContractsMap,
  IUnderwriterState,
  PlanToContractMap,
  TenderTypeMap,
  UnderwriterContentMap,
  UnderwritersMap,
} from '../models';
import { UnderwriterState } from './underwriter.state';

@Injectable({ providedIn: 'root' })
export class UnderwriterSelector {
  @Selector([UnderwriterState])
  static underwritersMap(state: IUnderwriterState): UnderwritersMap {
    return state.underwritersMap;
  }

  @Selector([UnderwriterState])
  static contractsMap(state: IUnderwriterState): ContractsMap {
    return state.contractsMap;
  }

  @Selector([UnderwriterState])
  static contractContentsMap(state: IUnderwriterState): ContractContentMap {
    return state.contractContentMap;
  }

  @Selector([UnderwriterState])
  static planToContractMap(state: IUnderwriterState): PlanToContractMap {
    return state.planToContractMap;
  }

  @Selector([UnderwriterState])
  static currentContractId(state: IUnderwriterState): number | undefined {
    return state.currentContractId;
  }

  @Selector([UnderwriterState])
  static tenderTypes(state: IUnderwriterState): ITenderType[] {
    const contractId = state.currentContractId;
    return contractId ? state.tenderTypeMap[contractId] : [];
  }

  @Selector([UnderwriterState])
  static tenderTypeMap(state: IUnderwriterState): TenderTypeMap {
    return state.tenderTypeMap;
  }

  /**
   * Selector to simplify getting the underwriterContents
   * NOTE: This can only be used after the plan is selected!
   * If you are getting an empty object, check:
   *  - the data has been loaded to the state
   *  - you are using underwriter content after the plan selector page
   */
  @Selector([
    UnderwriterSelector.currentContractId,
    UnderwriterSelector.contractContentsMap,
  ])
  static underwriterContent(
    contractId: number | undefined,
    contents: ContractContentMap
  ): UnderwriterContentMap {
    return contractId ? contents?.[contractId] : {};
  }

  public static translation(key: string): Translation {
    return createSelector(
      [UnderwriterSelector.underwriterContent],
      (state: UnderwriterContentMap) => {
        return state?.[key] || '';
      }
    );
  }
}
